import { tr } from "./lib/tr.js";
import { toast } from "./stores/toast.store.js";

/** Surface otherwise-silent runtime failures once React has mounted.
 *
 * The pre-mount handler in index.html intentionally goes quiet after #root
 * fills, so post-mount unhandled rejections / errors would vanish without a
 * trace. These listeners log the detail and show a single throttled toast so a
 * tester can report it, without spamming on a burst of related failures.
 *
 * Each error is also kept in a small in-memory ring buffer so the Developer
 * settings can show a human-readable log on-device (no DevTools-over-USB).
 */
let lastToastAt = 0;
const THROTTLE_MS = 5000;

/** One captured runtime error. */
export interface ErrorLogEntry {
  /** Epoch ms when it was captured. */
  at: number;
  /** Where it came from (e.g. "Uncaught error"). */
  label: string;
  /** The error message (or stringified reason). */
  message: string;
  /** The stack, when available. */
  stack?: string;
}

const MAX_ENTRIES = 50;
const log: ErrorLogEntry[] = [];
const listeners = new Set<() => void>();

function emit(): void {
  for (const l of listeners) l();
}

/** Newest-first snapshot of the captured runtime errors. */
export function getErrorLog(): ErrorLogEntry[] {
  return [...log].reverse();
}

export function clearErrorLog(): void {
  log.length = 0;
  emit();
}

/** Subscribe to error-log changes (returns an unsubscribe). */
export function subscribeErrorLog(fn: () => void): () => void {
  listeners.add(fn);
  return () => listeners.delete(fn);
}

function record(label: string, detail: unknown): void {
  const err = detail instanceof Error ? detail : null;
  const entry: ErrorLogEntry = {
    at: Date.now(),
    label,
    message: err ? err.message : String(detail),
  };
  if (err?.stack) entry.stack = err.stack;
  log.push(entry);
  if (log.length > MAX_ENTRIES) log.shift();
  emit();
}

function notify(label: string, detail: unknown): void {
  console.error(label, detail);
  record(label, detail);
  const now = Date.now();
  if (now - lastToastAt < THROTTLE_MS) return;
  lastToastAt = now;
  // `tr` only reads zustand state (no IO), so it is safe on the error path; it
  // falls back to English before the settings store hydrates.
  toast.error(tr("toast.genericError"));
}

export function installGlobalErrorHandlers(): void {
  if (typeof window === "undefined") return;
  window.addEventListener("unhandledrejection", (e) => {
    notify("Unhandled promise rejection", e.reason);
  });
  window.addEventListener("error", (e) => {
    // Resource load errors (img/script) don't carry an Error and aren't
    // actionable as a toast — only surface real script errors.
    if (e.error) notify("Uncaught error", e.error);
  });
}
